import { Box, colors, makeStyles, Paper, Theme, Typography, alpha, Grid } from '@material-ui/core';
import LoginHistoryItemDTO from 'dto/LoginHistoryItemDTO';
import moment, { Moment } from 'moment';
import React, { useMemo } from 'react';
import {XAxis, YAxis, Tooltip, ResponsiveContainer, Brush, AreaChart, Area, TooltipProps, Dot, CartesianGrid} from 'recharts';

const useClasses = makeStyles((theme: Theme) => ({
  chart: {
    fontFamily: theme.typography.fontFamily,
    '& *': {
      fontFamily: theme.typography.fontFamily
    },
    '& .recharts-brush-texts': {
      fontSize: '13px'
    }
  }
}));

interface LoginHistoryGraphProps {
  data: LoginHistoryItemDTO[]
}

interface LoginHistoryGraphItem {
  name: string,
  duration: number,
  sessionCount: number,
  sessions: {duration: number, loginDate: Moment}[]
}

const formatDuration = (duration: number) => {
  let out = '';
  if(duration > 59) {
    out += `${Math.floor(duration/60)}H `;
  }
  out += `${duration % 60}M`;

  return out;
}

const CustomTooltip = (props: TooltipProps<string, string>) => {
  const {active, payload} = props;
  if (active && payload && payload.length) {
    const {payload: val} = payload[0];
    if(val.duration) {
      return <Paper>
        <Box p={1}>
          <Typography><strong>{val.name}</strong></Typography>
          <Grid container>
            <Grid xs={6}><Typography><strong>Duration</strong></Typography></Grid>
            <Grid xs={6}><Typography><strong>Login Time</strong></Typography></Grid>
            {val.sessions.map((session: {duration: number, loginDate: Moment}) => <>
              <Grid xs={6}><Typography>{formatDuration(session.duration)}</Typography></Grid>
              <Grid xs={6}><Typography>{session.loginDate.local().format('HH:MM')}</Typography></Grid>
            </>)}
          </Grid>
        </Box>
      </Paper>
    }
  }

  return null;
}

const CustomDot = (props) => {
  // eslint-disable-next-line react/prop-types
  const payload = props.payload as {duration: number, name: string};
  if(!payload.duration) {
    return null;
  }

  return <Dot 
    {...props} 
    fill={colors.indigo[400]}
    fillOpacity={1}
    stroke={colors.indigo[400]}
  />
}

const LoginHistoryGraph = (props: LoginHistoryGraphProps) => {
  const {data} = props;

  const styles = useClasses();

  const finalData = useMemo<LoginHistoryGraphItem[]>(() => {
    if(!data.length) {
      return [];
    }
    const sortedData = [...data].sort((a,b) => a.login.isAfter(b.login) ? 1:-1);
    const oldestLogin = sortedData[0].login;
    const daysSinceOldestLogin = moment().diff(oldestLogin, 'days');
    return new Array(daysSinceOldestLogin).fill(null).map<LoginHistoryGraphItem>((_, offset) => {
      const d = oldestLogin.clone().add(offset, 'days');
      const out: LoginHistoryGraphItem = {
        name: d.local().format('MMM DD, YYYY'),
        duration: 0,
        sessionCount: 0,
        sessions: []
      };

      while(sortedData.length > 0 && sortedData[0].login.diff(d, 'days') === 0) {
        const item = sortedData.shift();
        if(item.logout.diff(item.login, 'days') > 0) {
          item.logout = item.login.clone().endOf('day');
          sortedData.unshift({
            duration: null, 
            id: item.id + '_', 
            login: item.login.clone().add(1, 'day').startOf('day'), 
            logout: item.logout.clone(), 
            expired: null,
            sessionCount: item.sessionCount 
          });
        }

        out.duration += item.logout.diff(item.login, 'minutes');
        out.sessionCount = item.sessionCount;
        out.sessions.push({loginDate: item.login, duration: item.logout.diff(item.login, 'minutes')});
      }

      if(!out.duration) {
        out.duration = null;
      }

      return out;
    })
  }, [data]);

  return <ResponsiveContainer 
    className={styles.chart}
    height='100%'
    width='100%'
  >
    <AreaChart data={finalData}>
      <XAxis
        dataKey='name'
        fontSize='12px'
        minTickGap={14}
        padding={{right: 20}}/>
      <YAxis
        domain={[0, 'auto']}
        fontSize='12px'
        padding={{top: 8}}
        tickFormatter={formatDuration}
        type='number' />
      <Tooltip content={<CustomTooltip/>}/>
      <CartesianGrid strokeDasharray={3} />
      <Brush
        dataKey='duration'
        startIndex={finalData.length-30}
        tickFormatter={(_ , idx) => moment().subtract(finalData.length-(idx-1), 'days').format('MMM DD, YYYY')} />
      <Area
        activeDot={{strokeWidth: 0, fill: colors.indigo[600]}}
        type="monotone"
        connectNulls
        dataKey='duration'
        stroke="#8884d8" 
        fillOpacity={1}
        dot={<CustomDot/>}
        fill={alpha(colors.indigo[50], 0.6)}
      />
    </AreaChart>
  </ResponsiveContainer>;
};

export default LoginHistoryGraph;